import { useRef, useState, useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { toBlob } from 'html-to-image';
import FormRadioBtn from 'components/common/forms/FormRadioBtn';
import SignaturePad from './SignaturePad';
import SingleBtn from 'components/common/elements/SingleBtn';
import { STYLES_VARIABLES } from 'constants/styleVariables';
import styles from 'styles/pages/modal/ParticipationAgreement.module.scss';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { AgreeParticipationFormDefaultValues } from 'constants/patient';
import { TAgreeParticipationForm } from 'types/form/patient';
import { getTodayDate, getTodayMonth, getTodayYear } from 'utils/date';
import useModal from 'hooks/useModal';

type TProps = {
  name: string;
  signImageURL: string | null;
  onSigned?: (imgURL: string | null) => void;
  onAgreeCompleted?: (file: File | null) => void;
  onClose?: () => void;
};

const ParticipationAgreement = ({
  name,
  signImageURL,
  onSigned,
  onAgreeCompleted,
  onClose,
}: TProps) => {
  const divRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { openModal, closeModal } = useModal();

  const methods = useForm<TAgreeParticipationForm>({
    defaultValues: AgreeParticipationFormDefaultValues,
  });
  const { handleSubmit, control } = methods;

  const agreeValue = useWatch({
    name: 'isAgree',
    control,
  });

  useEffect(() => {
    onSigned && agreeValue === 'disagree' && onSigned(null);
  }, [agreeValue]);

  const onClickSign = () => {
    closeModal();
    openModal({
      element: <SignaturePad imgURL={signImageURL} onSigned={onSigned} />,
    });
  };

  const onCloseModal = () => {
    onClose && onClose();
    closeModal();
  };

  const onValid = () => {
    if (!divRef.current) return;
    if (agreeValue !== 'agree' || !signImageURL) {
      onAgreeCompleted && onAgreeCompleted(null);
      closeModal();
      return;
    }
    setIsLoading(true);
    toBlob(divRef.current).then((blob) => {
      if (blob === null) return;
      const file = new File([blob], 'agreement.png', { type: 'image/png' });
      onAgreeCompleted && onAgreeCompleted(file);
    });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onValid)}>
        <div className="dimmed"></div>

        <div className={styles.popup_wrapper}>
          <div className="flex justify_end">
            <button type="button" onClick={onCloseModal}>
              <CloseIcon alt="close" fill={STYLES_VARIABLES.GRAY_07} />
            </button>
          </div>
          <div ref={divRef} className={styles.print_wrapper}>
            <h1 className={styles.title}>
              부산보건대학교 치위생과
              <br />
              치위생과정 실습 참여 동의서
            </h1>
            <p className={styles.text}>
              부산보건대학교 치위생과의 치위생과정 실습에 참여해주셔서
              감사드립니다.
              <br />
              부산보건대학교 치위생과는 인간존중과 전문성을 가진 치과위생사
              양성을
              <br />
              목적으로 다양한 프로그램을 시행하고자 아래 내용의 개인정보를
              제공받고자 합니다.
            </p>
            <div className={styles.text_wrapper}>
              <div className={styles.sub_text}>
                <span>1. </span>
                <span>
                  수입・이용 목적 : 부산보건대학교 치위생과정 실습 대상자 확인
                  및 관련 기록 관리
                </span>
              </div>
              <div className={styles.sub_text}>
                <span>2. </span>
                <span>
                  수집하려는 개인정보의 항목 : 성명, 성별, 전화번호, 직업,
                  관련기록 등 문진표
                </span>
              </div>
              <div className={styles.sub_text}>
                <span>3. </span>
                <span>개인정보의 보유 및 이용기간 : 5년</span>
              </div>
              <div
                className={styles.sub_text}
                style={{ display: 'flex', gap: '3px' }}
              >
                <span>4. </span>
                <span>
                  위의 1항에 열거한 목적 이외에는 제공받은 개인정보를 사용하지
                  않으며 개인 정보 보호법 제 3조 개인 정보 보호 원칙에 따라
                  수집된 개인정보를 보호합니다.
                </span>
              </div>
            </div>

            <p className="mt40 text_center fs14">
              본인은 상기의 내용에 대해 충분히 이해했으며 이에 동의합니다.
            </p>
            <div className="flex justify_center gap24 mt20">
              <FormRadioBtn name="isAgree" value="agree" labelText="동의" />
              <FormRadioBtn
                name="isAgree"
                value="disagree"
                labelText="비동의"
              />
            </div>

            <div className={styles.date_wrapper}>
              <div>
                {getTodayYear()}년 {getTodayMonth()}월 {getTodayDate()}일
              </div>
              <div className="mt20 relative">
                <span>이름:</span>
                <span className={styles.name_text}>{name}</span>
                <button
                  type="button"
                  className={styles.sign_btn}
                  onClick={onClickSign}
                  disabled={agreeValue === 'disagree'}
                >
                  (서명)
                </button>

                {signImageURL && (
                  <img
                    src={signImageURL}
                    alt="signature"
                    style={{
                      position: 'absolute',
                      maxWidth: '80px',
                      maxHeight: '36px',
                      bottom: '0',
                      right: '0',
                    }}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="flex justify_center">
            <SingleBtn
              type="submit"
              btnText={isLoading ? '등록 중...' : '대상자 등록'}
              isBtnActive={true}
              btnStyle={{
                marginTop: '32px',
              }}
              disabled={
                isLoading ||
                !agreeValue ||
                (agreeValue === 'agree' && !signImageURL)
              }
            />
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default ParticipationAgreement;
